import React, { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { frFR, enUS } from '@mui/x-date-pickers/locales';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';

import { useAddEventMutation, useUpdateEventMutation, useGetMyClubsQuery } from '../api/apiSlice';
import { useDecodedToken } from '../app/hooks';
import { FeedEvent } from '../types/api';

interface EventFormDialogProps {
    event?: FeedEvent;
    onClose: () => void;
    onSuccess?: () => void;
}

interface EventFormState {
    TITLE: string;
    LOCATION: string;
    DESCRIPTION: string;
}

const EventFormDialog = ({ event, onClose, onSuccess }: EventFormDialogProps) => {
    const { t, i18n } = useTranslation();
    const pickersLocaleText = (i18n.language === 'en' ? enUS : frFR).components.MuiLocalizationProvider.defaultProps.localeText;
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [date, setDate] = useState<Dayjs | null>(event ? dayjs(event.DATE) : null);
    const [image, setImage] = useState<File | null>(null);
    const [addEvent] = useAddEventMutation();
    const [updateEvent] = useUpdateEventMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(false);

    const decodedToken = useDecodedToken();
    const { data: myClubs = [] } = useGetMyClubsQuery();
    const managerClubs = myClubs.filter((club) => club.ROLE === 'ADMIN' || club.ROLE === 'ANIMATOR');
    const [clubId, setClubId] = useState<number | ''>(() => {
        if (event) return event.CLUB_ID;
        return '';
    });

    useEffect(() => {
        if (event || clubId !== '' || managerClubs.length === 0) return;
        const active = managerClubs.find((club) => club.CLUB_ID === decodedToken?.clubId);
        setClubId(active?.CLUB_ID ?? managerClubs[0].CLUB_ID);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myClubs]);

    const [form, setForm] = useState<EventFormState>({
        TITLE: event?.TITLE ?? '',
        LOCATION: event?.LOCATION ?? '',
        DESCRIPTION: event?.DESCRIPTION ?? '',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.TITLE || !date) return;
        if (!event && clubId === '') return;

        const formData = new FormData();
        formData.append('TITLE', form.TITLE);
        formData.append('DATE', date.format('YYYY-MM-DD HH:mm:ss'));
        formData.append('LOCATION', form.LOCATION);
        formData.append('DESCRIPTION', form.DESCRIPTION);
        if (!event && clubId !== '') formData.append('CLUB_ID', String(clubId));
        if (image) formData.append('image', image);

        setError(false);
        setIsSubmitting(true);
        try {
            if (event) {
                await updateEvent({ eventId: event.ID, formData }).unwrap();
            } else {
                await addEvent(formData).unwrap();
            }
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('error', error);
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs">
            <DialogTitle>{event ? t('eventFormDialog.editTitle') : t('eventFormDialog.addTitle')}</DialogTitle>
            <DialogContent>
                {error && (
                    <Box mt={1}>
                        <Alert severity="error">{t('common.genericError')}</Alert>
                    </Box>
                )}
                {!event && managerClubs.length > 1 && (
                    <Box mt={1} mb={1}>
                        <TextField
                            select
                            label={t('createActivity.club')}
                            value={clubId === '' ? '' : String(clubId)}
                            onChange={(e) => setClubId(Number(e.target.value))}
                            fullWidth
                            required
                        >
                            {managerClubs.map((club) => (
                                <MenuItem key={club.CLUB_ID} value={String(club.CLUB_ID)}>{club.NAME}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                )}
                <Box mt={1} mb={1}>
                    <TextField label={t('raceManagement.title')} name="TITLE" value={form.TITLE} onChange={handleInputChange} fullWidth required />
                </Box>
                <Box mb={1}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language} localeText={pickersLocaleText}>
                        <DateTimePicker
                            label={t('raceManagement.date')}
                            format="DD-MM-YYYY HH:mm"
                            value={date}
                            onChange={(value: Dayjs | null) => setDate(value)}
                            slotProps={{ textField: { fullWidth: true, required: true } }}
                        />
                    </LocalizationProvider>
                </Box>
                <Box mb={1}>
                    <TextField label={t('raceManagement.location')} name="LOCATION" value={form.LOCATION} onChange={handleInputChange} fullWidth />
                </Box>
                <Box mb={1}>
                    <TextField
                        label={t('eventFormDialog.description')}
                        name="DESCRIPTION"
                        value={form.DESCRIPTION}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </Box>
                <Box mb={1}>
                    <Button variant="contained" color="primary" onClick={() => imageInputRef.current?.click()}>
                        {image ? image.name : t('eventFormDialog.image')}
                    </Button>
                </Box>
                <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    ref={imageInputRef}
                    onChange={(e) => setImage(e.target.files?.[0] ?? null)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={isSubmitting}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                    {event ? t('common.save') : t('common.add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EventFormDialog;
